import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, ArrowRight } from "lucide-react";
import { Helmet } from "react-helmet-async";
import api from "../lib/api";

const BASE_URL = 'https://rtsexpress.co.uk';

function getImageUrl(url) {
  if (!url) return null;
  if (url.startsWith("/")) {
    return process.env.REACT_APP_BACKEND_URL + url;
  }
  return url;
}

function formatDate(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long", 
    year: "numeric"
  });
}

function extractYouTubeId(url) {
  if (!url) return null;
  const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
}

function extractVimeoId(url) {
  if (!url) return null;
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : null;
}

function isUploadedVideo(url) {
  if (!url) return false;
  return url.startsWith("/api/uploads/") || url.includes("/uploads/blog/");
}

function getVideoUrl(url) {
  if (!url) return null;
  if (url.startsWith("/")) {
    return process.env.REACT_APP_BACKEND_URL + url;
  }
  return url;
}

function BlogHeader(props) {
  return (
    <header className="bg-[#0B0F14]/95 border-b border-white/10 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#22c55e] to-[#16a34a] rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            </svg>
          </div>
          <div>
            <span className="text-white font-bold text-lg">RTS EXPRESS</span>
            <span className="text-slate-400 text-xs block">DRIVEN BY DELIVERY</span>
          </div>
        </Link>
        {props.onBack ? (
          <button onClick={props.onBack} className="flex items-center gap-2 text-slate-300 hover:text-white text-sm">
            <ArrowLeft className="w-4 h-4" />
            {props.backLabel || "Back"}
          </button>
        ) : (
          <Link to="/" className="flex items-center gap-2 text-slate-300 hover:text-white text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        )}
      </div>
    </header>
  );
}

/* Video player for card - autoplay muted */
function CardVideoPlayer({ url, autoplay = false }) {
  const videoRef = useRef(null);
  
  useEffect(() => {
    if (autoplay && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [autoplay]);

  if (isUploadedVideo(url)) {
    return (
      <video 
        ref={videoRef}
        src={getVideoUrl(url)}
        controls
        muted
        playsInline
        preload="metadata"
        loop
        className="w-full aspect-video object-cover bg-black"
      >
        Your browser does not support video playback.
      </video>
    );
  }
  
  const youtubeId = extractYouTubeId(url);
  if (youtubeId) {
    return (
      <iframe 
        width="100%" 
        className="aspect-video"
        src={`https://www.youtube.com/embed/${youtubeId}?autoplay=${autoplay ? 1 : 0}&mute=1`}
        title="Video"
        frameBorder="0" 
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
        allowFullScreen
      />
    );
  }
  
  const vimeoId = extractVimeoId(url);
  if (vimeoId) {
    return (
      <iframe 
        width="100%" 
        className="aspect-video"
        src={`https://player.vimeo.com/video/${vimeoId}?autoplay=${autoplay ? 1 : 0}&muted=1`}
        title="Video"
        frameBorder="0" 
        allow="autoplay; fullscreen" 
        allowFullScreen
      />
    );
  }
  
  return null;
}

/* Full video player for article detail */
function DetailVideoPlayer({ url }) {
  if (isUploadedVideo(url)) {
    return (
      <video 
        src={getVideoUrl(url)}
        controls
        playsInline
        preload="metadata"
        className="w-full aspect-video object-contain bg-black rounded-xl"
      >
        Your browser does not support video playback.
      </video>
    );
  }
  
  const youtubeId = extractYouTubeId(url);
  if (youtubeId) {
    return (
      <iframe 
        width="100%" 
        className="aspect-video rounded-xl"
        src={`https://www.youtube.com/embed/${youtubeId}`}
        title="Video"
        frameBorder="0" 
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
        allowFullScreen
      />
    );
  }
  
  const vimeoId = extractVimeoId(url);
  if (vimeoId) {
    return (
      <iframe 
        width="100%" 
        className="aspect-video rounded-xl"
        src={`https://player.vimeo.com/video/${vimeoId}`}
        title="Video"
        frameBorder="0" 
        allow="autoplay; fullscreen" 
        allowFullScreen
      />
    );
  }
  
  return null;
}

/* Individual Blog Article Page */
function BlogPostDetail({ post, onBack }) {
  const imageUrl = getImageUrl(post.featured_image);
  const hasVideo = post.videos && post.videos.length > 0;
  const primaryVideo = hasVideo ? post.videos[0] : null;
  const additionalVideos = hasVideo ? post.videos.slice(1) : [];
  const hasGallery = post.images && post.images.length > 1;
  
  return (
    <div className="min-h-screen bg-[#0B0F14]">
      <BlogHeader onBack={onBack} backLabel="Back to Blog" />
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">{post.title}</h1>
        
        {/* Date */}
        <div className="flex items-center gap-2 text-slate-400 text-sm mb-8">
          <Calendar className="w-4 h-4" />
          {formatDate(post.published_at)}
        </div>
        
        {/* Primary Media - Video takes priority */}
        {primaryVideo ? (
          <div className="mb-8 rounded-xl overflow-hidden">
            <DetailVideoPlayer url={primaryVideo} />
          </div>
        ) : imageUrl ? (
          <img 
            src={imageUrl} 
            alt={post.title} 
            className="w-full h-auto max-h-[500px] object-cover rounded-xl mb-8"
          />
        ) : null}
        
        {/* Content */}
        <div className="text-slate-300 whitespace-pre-wrap leading-relaxed text-lg mb-10">
          {post.content}
        </div>
        
        {/* Additional Videos */}
        {additionalVideos.length > 0 && (
          <div className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">More Videos</h3>
            <div className="space-y-4">
              {additionalVideos.map((videoUrl, idx) => (
                <div key={idx} className="rounded-xl overflow-hidden">
                  <DetailVideoPlayer url={videoUrl} />
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Image Gallery */}
        {hasGallery && (
          <div className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">Gallery</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {post.images.filter(img => img !== post.featured_image).map((imgSrc, idx) => (
                <img 
                  key={idx} 
                  src={getImageUrl(imgSrc)} 
                  alt={`Gallery ${idx + 1}`}
                  className="w-full h-40 object-cover rounded-lg"
                />
              ))}
            </div>
          </div>
        )}
      </main>
      <footer className="bg-[#0a0d12] border-t border-white/10 py-8 px-6 text-center text-slate-400 text-sm mt-12">
        <p>© 2026 RTS EXPRESS LTD. All rights reserved.</p>
      </footer>
    </div>
  );
}

/* Featured/Hero Blog Card - Large format with video/image */
function FeaturedBlogCard({ post, onSelect }) {
  const imageUrl = getImageUrl(post.featured_image);
  const hasVideo = post.videos && post.videos.length > 0;
  const primaryVideo = hasVideo ? post.videos[0] : null;
  
  return (
    <article className="bg-slate-800/30 border border-white/10 rounded-2xl overflow-hidden mb-8">
      <div className="p-6 md:p-8">
        {/* Title at top */}
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{post.title}</h2>
        
        {/* Date */}
        <div className="flex items-center gap-2 text-slate-400 text-sm mb-6">
          <Calendar className="w-4 h-4" />
          {formatDate(post.published_at)}
        </div>
        
        {/* Media - Video takes priority, autoplay muted */}
        <div className="rounded-xl overflow-hidden mb-6 bg-black">
          {primaryVideo ? (
            <CardVideoPlayer url={primaryVideo} autoplay={true} />
          ) : imageUrl ? (
            <img 
              src={imageUrl} 
              alt={post.title}
              className="w-full aspect-video object-cover"
            />
          ) : (
            <div className="w-full aspect-video bg-slate-700 flex items-center justify-center">
              <span className="text-slate-400">No media</span>
            </div>
          )}
        </div>
        
        {/* Short description / excerpt */}
        <p className="text-slate-300 text-base leading-relaxed mb-6 line-clamp-3">
          {post.excerpt || post.content?.substring(0, 200)}
        </p>
        
        {/* Read full article button */}
        <button 
          onClick={() => onSelect(post)}
          className="w-full flex items-center justify-between px-5 py-4 bg-slate-800/60 hover:bg-slate-700/60 border border-white/10 rounded-xl transition-colors group"
        >
          <span className="text-white font-medium">Read full article</span>
          <ArrowRight className="w-5 h-5 text-[#22c55e] group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </article>
  );
}

/* Secondary Blog Card - Smaller horizontal format */
function SecondaryBlogCard({ post, onSelect }) {
  const imageUrl = getImageUrl(post.featured_image);
  const hasVideo = post.videos && post.videos.length > 0;
  const primaryVideo = hasVideo ? post.videos[0] : null;
  
  // For secondary cards, show video thumbnail or image
  const thumbnailUrl = imageUrl;
  
  return (
    <article 
      className="bg-slate-800/30 border border-white/10 rounded-xl overflow-hidden hover:border-[#22c55e]/50 transition-all cursor-pointer group"
      onClick={() => onSelect(post)}
    >
      <div className="flex flex-col sm:flex-row">
        {/* Thumbnail - video frame or image */}
        <div className="sm:w-48 md:w-56 flex-shrink-0 bg-slate-900">
          {primaryVideo && isUploadedVideo(primaryVideo) ? (
            <video 
              src={getVideoUrl(primaryVideo)}
              muted
              playsInline
              preload="metadata"
              className="w-full h-40 sm:h-full object-cover"
            />
          ) : thumbnailUrl ? (
            <img 
              src={thumbnailUrl} 
              alt={post.title}
              className="w-full h-40 sm:h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-40 sm:h-full bg-slate-700 flex items-center justify-center">
              <span className="text-slate-500 text-sm">No image</span>
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="p-5 flex-1 flex flex-col justify-between">
          <div>
            <h3 className="text-lg font-bold text-white mb-1 group-hover:text-[#22c55e] transition-colors line-clamp-2">
              {post.title}
            </h3>
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-3">
              <Calendar className="w-3 h-3" />
              {formatDate(post.published_at)}
            </div>
            <p className="text-slate-400 text-sm line-clamp-3">
              {post.excerpt || post.content?.substring(0, 150)}
            </p>
          </div>
          <div className="mt-4">
            <span className="inline-flex items-center gap-1 text-[#22c55e] text-sm font-medium group-hover:gap-2 transition-all">
              Read article <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    api.get("/blog/posts")
      .then(response => {
        setPosts(response.data || []);
      })
      .catch(() => {});
    
    // Load Soro embed
    const script = document.createElement("script");
    script.src = "https://app.trysoro.com/api/embed/2c6b50af-7ff8-452c-b759-9688a41adb2e";
    script.defer = true;
    document.body.appendChild(script);
    
    // Add Soro styles
    const style = document.createElement("style");
    style.id = "soro-blog-styles";
    style.textContent = "#soro-blog,#soro-blog *{color:#e2e8f0!important}#soro-blog h1,#soro-blog h2,#soro-blog h3{color:#fff!important}#soro-blog a{color:#22c55e!important}";
    document.head.appendChild(style);
    
    return () => {
      const existingScript = document.querySelector('script[src*="trysoro"]');
      if (existingScript) existingScript.remove();
      const existingStyle = document.getElementById("soro-blog-styles");
      if (existingStyle) existingStyle.remove();
    };
  }, []);

  if (selectedPost) {
    return (
      <>
        <Helmet>
          <title>{selectedPost.title} | RTS EXPRESS Blog</title>
          <meta name="description" content={selectedPost.excerpt || selectedPost.content?.substring(0, 160)} />
          <link rel="canonical" href={`${BASE_URL}/blog`} />
          <meta property="og:title" content={selectedPost.title} />
          <meta property="og:description" content={selectedPost.excerpt || selectedPost.content?.substring(0, 160)} />
          <meta property="og:url" content={`${BASE_URL}/blog`} />
        </Helmet>
        <BlogPostDetail 
          post={selectedPost} 
          onBack={() => setSelectedPost(null)} 
        />
      </>
    );
  }

  // Separate featured (first/newest) and secondary posts
  const featuredPost = posts.length > 0 ? posts[0] : null;
  const secondaryPosts = posts.slice(1);

  return (
    <div className="min-h-screen bg-[#0B0F14]">
      <Helmet>
        <title>Blog | RTS EXPRESS - Same Day Courier News & Updates</title>
        <meta name="description" content="Latest news, insights and updates from RTS Express - UK's trusted same-day courier service. Transport tips, industry news and company updates." />
        <link rel="canonical" href={`${BASE_URL}/blog`} />
        <meta property="og:title" content="Blog | RTS EXPRESS" />
        <meta property="og:description" content="Latest news and updates from RTS Express - UK's trusted same-day courier service." />
        <meta property="og:url" content={`${BASE_URL}/blog`} />
      </Helmet>
      <BlogHeader />
      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Page Title */}
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Blog</h1>
          <p className="text-slate-400">Latest news and updates from RTS Express</p>
        </div>
        
        {/* Featured Post - Large card with video/image */}
        {featuredPost && (
          <FeaturedBlogCard post={featuredPost} onSelect={setSelectedPost} />
        )}
        
        {/* Secondary Posts - Grid of smaller cards */}
        {secondaryPosts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {secondaryPosts.map(post => (
              <SecondaryBlogCard 
                key={post.id} 
                post={post} 
                onSelect={setSelectedPost} 
              />
            ))}
          </div>
        )}
        
        {/* Soro embed */}
        <div id="soro-blog" className="min-h-[400px]"></div>
      </main>
      <footer className="bg-[#0a0d12] border-t border-white/10 py-8 px-6 text-center text-slate-400 text-sm">
        <p>© 2026 RTS EXPRESS LTD. All rights reserved.</p>
      </footer>
    </div>
  );
}
